import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  Suspense,
} from "react";
import ProductCard from "../shop/ProductCard";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useProductsByIds from "../../hooks/useProductsByIds";
import QuickViewModal from "../shop/QuickViewModal";
import { SkeletonHorizontalScroll } from "./SkeletonLoader";
import LightLoader from "./LightLoader";

// Utility function to get responsive card width
const getCardWidth = () => {
  if (typeof window === "undefined") return 224; // Default for SSR
  const width = window.innerWidth;
  if (width < 480) return 140; // Extra small screens
  if (width < 640) return 160; // Small screens
  if (width < 768) return 192; // Medium screens
  if (width < 1024) return 224; // Large screens
  if (width < 1280) return 256; // Extra large screens
  return 288; // 2XL screens
};

// Utility function to get cards per view
const getCardsPerView = () => {
  if (typeof window === "undefined") return 3; // Default for SSR
  const width = window.innerWidth;
  if (width < 480) return 2; // Extra small screens
  if (width < 640) return 2; // Small screens
  if (width < 768) return 3; // Medium screens
  if (width < 1024) return 4; // Large screens
  if (width < 1280) return 5; // Extra large screens
  return 6; // 2XL screens
};

const HorizontalProductScroll = React.memo(
  ({
    title,
    subtitle,
    tagline,
    description,
    topSellingProducts,
    newProducts,
    viewAllLink,
    viewAllText,
    tab1Label = "Top Selling",
    tab2Label = "Top Featured",
  }) => {
    const [quickViewOpen, setQuickViewOpen] = useState(false);
    const [quickViewProduct, setQuickViewProduct] = useState(null);
    const scrollContainerRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const [activeTab, setActiveTab] = useState("top-selling"); // 'top-selling' or 'featured'
    const [isAutoScrolling, setIsAutoScrolling] = useState(true);
    const autoScrollIntervalRef = useRef(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [windowSize, setWindowSize] = useState({
      width: typeof window !== "undefined" ? window.innerWidth : 1024,
      height: typeof window !== "undefined" ? window.innerHeight : 768,
    });
    const [isVisible, setIsVisible] = useState(true);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    // Fetch both top selling and new products with optimized loading
    const { products: topSellingData, loading: topSellingLoading } =
      useProductsByIds(topSellingProducts);
    const { products: newProductsData, loading: newProductsLoading } =
      useProductsByIds(newProducts);

    // Get products based on active tab
    const currentProducts = useMemo(() => {
      if (activeTab === "top-selling") {
        return topSellingData || [];
      } else {
        return newProductsData || [];
      }
    }, [activeTab, topSellingData, newProductsData]);

    const loading =
      activeTab === "top-selling" ? topSellingLoading : newProductsLoading;

    // Add loading state when switching tabs
    const [isTabSwitching, setIsTabSwitching] = useState(false);

    // Check scroll position for arrow visibility
    const checkScrollPosition = useCallback(() => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          scrollContainerRef.current;
        const shouldShowLeft = scrollLeft > 0;
        const shouldShowRight = scrollLeft < scrollWidth - clientWidth - 5;

        setShowLeftArrow(shouldShowLeft);
        setShowRightArrow(shouldShowRight);
      }
    }, [currentProducts.length]);

    // Handle window resize
    useEffect(() => {
      const handleResize = () => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
        // Recalculate scroll position after resize
        setTimeout(checkScrollPosition, 100);
      };

      // Handle visibility change (tab switching, minimizing)
      const handleVisibilityChange = () => {
        setIsVisible(!document.hidden);
        if (document.hidden) {
          setIsAutoScrolling(false);
        } else {
          // Resume auto-scroll after 2 seconds when tab becomes visible
          setTimeout(() => setIsAutoScrolling(true), 2000);
        }
      };

      window.addEventListener("resize", handleResize);
      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        window.removeEventListener("resize", handleResize);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }, [checkScrollPosition]);

    // Pause auto-scroll on user interaction
    const handleUserInteraction = useCallback(() => {
      setIsAutoScrolling(false);
      // Resume auto-scroll after 5 seconds of no interaction
      setTimeout(() => setIsAutoScrolling(true), 5000);
    }, []);

    // Enhanced scroll handlers with step-by-step animations
    const scrollLeft = useCallback(() => {
      if (isAnimating) return; // Prevent multiple animations

      handleUserInteraction(); // Pause auto-scroll
      setIsAnimating(true);

      if (scrollContainerRef.current) {
        const cardWidth = getCardWidth();
        const cardsPerView = getCardsPerView();

        scrollContainerRef.current.scrollBy({
          left: -(cardWidth * cardsPerView),
          behavior: "smooth",
        });

        // Update step counter
        setCurrentStep((prev) => Math.max(0, prev - cardsPerView));

        // Reset animation state after animation completes
        setTimeout(() => {
          setIsAnimating(false);
        }, 600); // Match CSS transition duration
      }
    }, [handleUserInteraction, isAnimating]);

    const scrollRight = useCallback(() => {
      if (isAnimating) return; // Prevent multiple animations

      handleUserInteraction(); // Pause auto-scroll
      setIsAnimating(true);

      if (scrollContainerRef.current) {
        const cardWidth = getCardWidth();
        const cardsPerView = getCardsPerView();

        scrollContainerRef.current.scrollBy({
          left: cardWidth * cardsPerView,
          behavior: "smooth",
        });

        // Update step counter
        setCurrentStep((prev) => prev + cardsPerView);

        // Reset animation state after animation completes
        setTimeout(() => {
          setIsAnimating(false);
        }, 600); // Match CSS transition duration
      }
    }, [handleUserInteraction, isAnimating]);

    // Memoized Quick View handler
    const handleQuickView = useCallback((product) => {
      setQuickViewProduct(product);
      setQuickViewOpen(true);
    }, []);

    // Enhanced auto-scroll function with step-by-step animations
    const autoScroll = useCallback(() => {
      if (
        isAnimating ||
        !scrollContainerRef.current ||
        currentProducts.length === 0 ||
        !isVisible
      )
        return;

      setIsAnimating(true);
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;

      // If we've reached the end, scroll back to the beginning
      if (scrollLeft >= scrollWidth - clientWidth - 5) {
        scrollContainerRef.current.scrollTo({
          left: 0,
          behavior: "smooth",
        });
        setCurrentStep(0);
      } else {
        // Scroll one card width
        const cardWidth = getCardWidth();
        scrollContainerRef.current.scrollBy({
          left: cardWidth,
          behavior: "smooth",
        });
        setCurrentStep((prev) => prev + 1);
      }

      // Reset animation state after animation completes
      setTimeout(() => {
        setIsAnimating(false);
      }, 600); // Match CSS transition duration
    }, [currentProducts.length, isAnimating, isVisible]);

    // Start auto-scroll interval with responsive timing
    useEffect(() => {
      if (
        isAutoScrolling &&
        currentProducts.length > 0 &&
        !loading &&
        isVisible
      ) {
        // Adjust interval based on screen size and user preference
        const interval = windowSize.width < 768 ? 1000 : 1000; // 1 second for all devices
        autoScrollIntervalRef.current = setInterval(autoScroll, interval);

        return () => {
          if (autoScrollIntervalRef.current) {
            clearInterval(autoScrollIntervalRef.current);
          }
        };
      }
    }, [
      isAutoScrolling,
      currentProducts.length,
      loading,
      autoScroll,
      isVisible,
      windowSize.width,
    ]);

    // Add scroll event listener and touch support
    useEffect(() => {
      const container = scrollContainerRef.current;
      if (container) {
        const handleScroll = () => {
          checkScrollPosition();
          handleUserInteraction(); // Pause auto-scroll on manual scroll
        };

        // Touch event handlers for mobile swipe
        const handleTouchStart = (e) => {
          setTouchStart(e.targetTouches[0].clientX);
        };

        const handleTouchMove = (e) => {
          setTouchEnd(e.targetTouches[0].clientX);
        };

        const handleTouchEnd = () => {
          if (!touchStart || !touchEnd) return;

          const distance = touchStart - touchEnd;
          const isLeftSwipe = distance > 50;
          const isRightSwipe = distance < -50;

          if (isLeftSwipe) {
            scrollRight();
          } else if (isRightSwipe) {
            scrollLeft();
          }

          setTouchStart(null);
          setTouchEnd(null);
        };

        container.addEventListener("scroll", handleScroll);
        container.addEventListener("touchstart", handleTouchStart, {
          passive: true,
        });
        container.addEventListener("touchmove", handleTouchMove, {
          passive: true,
        });
        container.addEventListener("touchend", handleTouchEnd, {
          passive: true,
        });

        checkScrollPosition(); // Initial check

        return () => {
          container.removeEventListener("scroll", handleScroll);
          container.removeEventListener("touchstart", handleTouchStart);
          container.removeEventListener("touchmove", handleTouchMove);
          container.removeEventListener("touchend", handleTouchEnd);
        };
      }
    }, [
      checkScrollPosition,
      handleUserInteraction,
      scrollLeft,
      scrollRight,
      touchStart,
      touchEnd,
    ]);

    // Check arrow visibility when products load
    useEffect(() => {
      if (!loading && currentProducts.length > 0) {
        setTimeout(checkScrollPosition, 100); // Small delay to ensure DOM is ready
      }
    }, [loading, currentProducts.length, checkScrollPosition]);

    // Reset scroll position when tab changes
    useEffect(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = 0;
        setTimeout(checkScrollPosition, 100);
        // Reset auto-scroll and step counter when tab changes
        setIsAutoScrolling(true);
        setCurrentStep(0);
        setIsAnimating(false);
      }
    }, [activeTab, checkScrollPosition]);

    // Keyboard navigation support
    useEffect(() => {
      const handleKeyDown = (e) => {
        if (!scrollContainerRef.current) return;

        switch (e.key) {
          case "ArrowLeft":
            e.preventDefault();
            scrollLeft();
            break;
          case "ArrowRight":
            e.preventDefault();
            scrollRight();
            break;
          case "Home":
            e.preventDefault();
            scrollContainerRef.current.scrollTo({
              left: 0,
              behavior: "smooth",
            });
            setCurrentStep(0);
            handleUserInteraction();
            break;
          case "End":
            e.preventDefault();
            if (scrollContainerRef.current) {
              const { scrollWidth, clientWidth } = scrollContainerRef.current;
              scrollContainerRef.current.scrollTo({
                left: scrollWidth - clientWidth,
                behavior: "smooth",
              });
              setCurrentStep(
                Math.ceil(currentProducts.length / getCardsPerView()) - 1
              );
              handleUserInteraction();
            }
            break;
          default:
            break;
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [
      scrollLeft,
      scrollRight,
      handleUserInteraction,
      currentProducts.length,
    ]);

    return (
      <section className="py-8 sm:py-12 lg:py-16 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header - Responsive */}
          <div className="text-center mb-8 sm:mb-12">
            {tagline && (
              <div className="inline-flex items-center px-3 sm:px-4 py-1 sm:py-2 bg-gradient-to-r from-primary-50 to-accent-50 border border-primary-100 mb-3 sm:mb-4 rounded-full">
                <span className="text-xs sm:text-sm font-medium text-primary-700">
                  {tagline}
                </span>
              </div>
            )}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-800 mb-4 sm:mb-6">
              {title}{" "}
              {subtitle && <span className="text-gray-600">{subtitle}</span>}
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
              {description}
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="inline-flex bg-gray-100 p-1 shadow-sm">
              <button
                onClick={() => {
                  setIsTabSwitching(true);
                  setActiveTab("top-selling");
                  setTimeout(() => setIsTabSwitching(false), 300);
                }}
                disabled={loading || isTabSwitching}
                className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  activeTab === "top-selling"
                    ? "bg-primary-600 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {tab1Label}
              </button>
              <button
                onClick={() => {
                  setIsTabSwitching(true);
                  setActiveTab("featured");
                  setTimeout(() => setIsTabSwitching(false), 300);
                }}
                disabled={loading || isTabSwitching}
                className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  activeTab === "featured"
                    ? "bg-primary-600 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {tab2Label}
              </button>
            </div>
          </div>

          {/* Horizontal Scroll Container */}
          <div
            className="relative horizontal-scroll-container"
            style={{ overflow: "hidden" }}
          >
            {/* Left Arrow */}
            {showLeftArrow && currentProducts.length > 0 && (
              <button
                onClick={scrollLeft}
                disabled={isAnimating}
                className={`absolute left-1 sm:left-2 top-1/2 transform -translate-y-1/2 z-10 bg-black/80 backdrop-blur-sm rounded-full p-2 shadow-lg transition-all duration-300 hover:shadow-xl scroll-arrow ${
                  isAnimating
                    ? "opacity-50 cursor-not-allowed scale-95"
                    : "hover:bg-black hover:scale-110"
                }`}
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-4 h-4 text-white" />
              </button>
            )}

            {/* Right Arrow */}
            {showRightArrow && currentProducts.length > 0 && (
              <button
                onClick={scrollRight}
                disabled={isAnimating}
                className={`absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 z-10 bg-black/80 backdrop-blur-sm rounded-full p-2 shadow-lg transition-all duration-300 hover:shadow-xl scroll-arrow ${
                  isAnimating
                    ? "opacity-50 cursor-not-allowed scale-95"
                    : "hover:bg-black hover:scale-110"
                }`}
                aria-label="Scroll right"
              >
                <ChevronRight className="w-4 h-4 text-white" />
              </button>
            )}

            {/* Product Scroll Container */}
            <div className="relative">
              <div
                className={`transition-opacity duration-300 ${
                  isTabSwitching ? "opacity-50" : "opacity-100"
                }`}
              >
                {loading ? (
                  <div className="flex justify-center items-center h-64">
                    <LightLoader size="lg" />
                  </div>
                ) : currentProducts.length === 0 ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                      <p className="text-gray-500 text-lg mb-2">
                        No products available
                      </p>
                      <p className="text-gray-400 text-sm">
                        Please check back later
                      </p>
                    </div>
                  </div>
                ) : (
                  <Suspense fallback={<SkeletonHorizontalScroll items={4} />}>
                    <div
                      ref={scrollContainerRef}
                      className={`flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4 px-2 transition-all duration-600 ease-out ${
                        isAnimating ? "pointer-events-none" : ""
                      }`}
                      style={{
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        scrollBehavior: "smooth",
                      }}
                    >
                      {currentProducts.map((product) => (
                        <div
                          key={product.id}
                          className="flex-shrink-0 w-40 sm:w-48 md:w-56 lg:w-64 xl:w-72 h-full"
                        >
                          <ProductCard
                            product={product}
                            onQuickView={handleQuickView}
                            className="h-full"
                          />
                        </div>
                      ))}
                    </div>
                  </Suspense>
                )}
              </div>
            </div>

            {/* Step Indicators */}
            {currentProducts.length > 0 && (
              <div className="flex justify-center mt-4 space-x-2">
                {Array.from(
                  {
                    length: Math.ceil(
                      currentProducts.length / getCardsPerView()
                    ),
                  },
                  (_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        if (scrollContainerRef.current) {
                          const cardWidth = getCardWidth();
                          const cardsPerView = getCardsPerView();
                          const targetScroll = index * cardWidth * cardsPerView;
                          scrollContainerRef.current.scrollTo({
                            left: targetScroll,
                            behavior: "smooth",
                          });
                          setCurrentStep(index * cardsPerView);
                          handleUserInteraction();
                        }
                      }}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        Math.floor(currentStep / getCardsPerView()) === index
                          ? "bg-primary-600 scale-125"
                          : "bg-gray-300 hover:bg-gray-400"
                      }`}
                      aria-label={`Go to step ${index + 1}`}
                    />
                  )
                )}
              </div>
            )}

            {/* View All Button */}
            <div className="text-center mt-8">
              <a
                href={viewAllLink}
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors duration-200 shadow-lg hover:shadow-xl"
              >
                {viewAllText}
                <ChevronRight className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
        </div>

        {/* Quick View Modal */}
        {quickViewOpen && quickViewProduct && (
          <QuickViewModal
            product={quickViewProduct}
            open={quickViewOpen}
            onClose={() => setQuickViewOpen(false)}
          />
        )}
      </section>
    );
  }
);

HorizontalProductScroll.displayName = "HorizontalProductScroll";

export default HorizontalProductScroll;
